import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  TbPlus,
  TbEdit,
  TbTrash,
  TbPig,
  TbUsers,
  TbAlertCircle,
  TbArrowDown,
  TbArrowUp,
  TbArrowRight,
} from 'react-icons/tb';

import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import ModalLayout from '../../components/layouts/ModalLayout';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import CurrencyInput from '../../components/forms/CurrencyInput';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import { formatBalance } from '../../../utils/bankAccountUtil';
import * as bankAccountService from '../../../services/community/bankAccountService';
import potService, { type PotWithDetails, type UpdatePotDto } from '../../../services/community/potService';
import { SavingPot, Currency, CreateSavingPotDto } from '../../../types/bankAccount';
import useSelectedDashboardId from '../../../hooks/community/useSelectedDashboardId';

interface PotParticipant {
  id: number;
  name: string;
  balance: number; // in cents
  isActive: boolean;
}

interface PotWithBalances extends SavingPot {
  totalBalance: number; // in cents (fetched from API)
  participants: PotParticipant[]; // member balances
   participantIds: number[]; // dashboard member ids linked to this pot
  color: string; // UI-only field for display
}

interface PotsPageProps {
  hideHeader?: boolean;
}

function mapPotWithDetailsToWithBalances(
  pot: PotWithDetails & { savingPotId?: number; dashboardMemberIds?: number[] },
): PotWithBalances {
  const participants = Array.isArray(pot.participants) ? pot.participants : [];
  const potId = pot.potId ?? pot.savingPotId ?? 0;
  const participantIds =
    Array.isArray(pot.dashboardMemberIds) && pot.dashboardMemberIds.length > 0
      ? pot.dashboardMemberIds
      : participants.map((p) => p.dashboardMemberId);
  return {
    savingPotId: potId,
    dashboardId: pot.dashboardId,
    name: pot.name,
    targetAmount: pot.targetAmount ?? null,
    currency: pot.currency,
    isActive: !pot.completedAt,
    ledgerAccountId: pot.ledgerAccountId,
    totalBalance: pot.totalBalance ?? 0,
    participants: participants.map((p) => ({
      id: p.dashboardMemberId,
      name: p.name,
      balance: p.currentBalance ?? 0,
      isActive: p.isActive ?? true,
    })),
    participantIds,
    color: pot.color ?? '#3B82F6',
  };
}

export default function PotsPage({ hideHeader = false }: PotsPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.pots' });
  const navigate = useNavigate();
  const dashboardId = useSelectedDashboardId();

  const [pots, setPots] = useState<PotWithBalances[]>([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBreakModalOpen, setIsBreakModalOpen] = useState(false);
  const [isFundModalOpen, setIsFundModalOpen] = useState(false);
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
  const [editingPot, setEditingPot] = useState<PotWithBalances | null>(null);
  const [breakingPot, setBreakingPot] = useState<PotWithBalances | null>(null);
  const [fundingPot, setFundingPot] = useState<PotWithBalances | null>(null);
  const [releasingPot, setReleasingPot] = useState<PotWithBalances | null>(null);
  const [newPot, setNewPot] = useState({
    name: '',
    color: '#3B82F6',
    targetAmount: '',
    currency: Currency.EUR,
  });
  const [newPotTargetAmountCents, setNewPotTargetAmountCents] = useState<number | null>(null);
  const [fundAmount, setFundAmount] = useState('');
  const [releaseAmount, setReleaseAmount] = useState('');
  const [isRunningAutoDeposits, setIsRunningAutoDeposits] = useState(false);

  const loadPots = useCallback(async () => {
    if (dashboardId == null) return;
    try {
      setLoading(true);
      const list = await potService.listPots(dashboardId, true);
      const potsWithBalances = list.map(mapPotWithDetailsToWithBalances);
      setPots(potsWithBalances);
    } catch (error: any) {
      console.error('Error loading saving pots:', error);
      toast.error(error?.response?.data?.message || t('errors.loadFailed') || 'Failed to load saving pots');
    } finally {
      setLoading(false);
    }
  }, [dashboardId, t]);

  useEffect(() => {
    loadPots();
  }, [loadPots]);

  const colorOptions = [
    { label: 'Blue', value: '#3B82F6' },
    { label: 'Green', value: '#10B981' },
    { label: 'Yellow', value: '#F59E0B' },
    { label: 'Red', value: '#EF4444' },
    { label: 'Purple', value: '#8B5CF6' },
    { label: 'Pink', value: '#EC4899' },
    { label: 'Indigo', value: '#6366F1' },
    { label: 'Teal', value: '#14B8A6' },
  ];

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'text-success bg-green-100' : 'text-danger bg-red-100';
  };

  const getStatusIcon = (isActive: boolean) => {
    return isActive ? <TbPig className="h-4 w-4" /> : <TbAlertCircle className="h-4 w-4" />;
  };

  const getStatusLabel = (isActive: boolean) => {
    return isActive ? t('status.active') : t('status.broken');
  };

  const handleRunSavingPotAutoDeposits = async () => {
    if (!pots.length) {
      toast.warning(t('errors.noPotsToRun') || 'No saving pots with auto-deposits to run');
      return;
    }

    if (
      !window.confirm(
        t('confirmations.runAutoDeposits') ||
          'Run automatic deposits for all eligible saving pots? This will create journal entries.',
      )
    ) {
      return;
    }

    try {
      setIsRunningAutoDeposits(true);
      const result = await bankAccountService.runSavingPotAutoDeposits();
      toast.success(
        t('success.autoDepositsRun', {
          posted: result.posted,
          skipped: result.skipped,
        }) ||
          `Posted ${result.posted} auto-deposits, skipped ${result.skipped} entries that were already processed`,
      );
      await loadPots();
    } catch (error: any) {
      console.error('Error running saving pot auto-deposits:', error);
      toast.error(
        error?.response?.data?.message ||
          t('errors.runAutoDepositsFailed') ||
          'Failed to run saving pot auto-deposits',
      );
    } finally {
      setIsRunningAutoDeposits(false);
    }
  };

  const handleCreatePot = async () => {
    if (!newPot.name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }

    try {
      const createDto: CreateSavingPotDto = {
        name: newPot.name.trim(),
        currency: newPot.currency,
        targetAmount: newPotTargetAmountCents ?? undefined,
        isActive: true,
      };

      await bankAccountService.createSavingPot(createDto);
      toast.success(t('success.created') || 'Saving pot created successfully');
      setNewPot({ name: '', color: '#3B82F6', targetAmount: '', currency: Currency.EUR });
      setNewPotTargetAmountCents(null);
      setIsCreateModalOpen(false);
      loadPots();
    } catch (error: any) {
      console.error('Error creating saving pot:', error);
      toast.error(
        error?.response?.data?.message || t('errors.createFailed') || 'Failed to create saving pot',
      );
    }
  };

  const handleEditPot = async () => {
    if (!editingPot || !editingPot.name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }

    try {
      const updateDto: UpdatePotDto = {
        name: editingPot.name.trim(),
        color: editingPot.color,
        targetAmount: editingPot.targetAmount ?? undefined,
      };

      await potService.updatePot(dashboardId!, editingPot.savingPotId, updateDto);
      toast.success(t('success.updated') || 'Saving pot updated successfully');
      setEditingPot(null);
      setIsEditModalOpen(false);
      loadPots();
    } catch (error: any) {
      console.error('Error updating saving pot:', error);
      toast.error(
        error?.response?.data?.message || t('errors.updateFailed') || 'Failed to update saving pot',
      );
    }
  };

  const handleDeletePot = async (pot: PotWithBalances) => {
    if (window.confirm(t('confirmations.delete', { name: pot.name }) || `Delete ${pot.name}?`)) {
      try {
        await bankAccountService.deleteSavingPot(pot.savingPotId);
        toast.success(t('success.deleted') || 'Saving pot deactivated successfully');
        loadPots();
      } catch (error: any) {
        console.error('Error deleting saving pot:', error);
        toast.error(
          error?.response?.data?.message || t('errors.deleteFailed') || 'Failed to delete saving pot',
        );
      }
    }
  };

  const handleBreakPot = async () => {
    if (!breakingPot) return;

    if (window.confirm(t('confirmations.break', { name: breakingPot.name }) || `Break ${breakingPot.name}?`)) {
      try {
        await bankAccountService.updateSavingPot(breakingPot.savingPotId, { isActive: false });
        toast.success(t('success.broken') || 'Saving pot deactivated successfully');
        setBreakingPot(null);
        setIsBreakModalOpen(false);
        loadPots();
      } catch (error: any) {
        console.error('Error breaking saving pot:', error);
        toast.error(
          error?.response?.data?.message || t('errors.breakFailed') || 'Failed to deactivate saving pot',
        );
      }
    }
  };

  const handleFundPot = async () => {
    if (!fundingPot || !fundAmount) {
      toast.error(t('errors.amountRequired') || 'Amount is required');
      return;
    }

    const amountInCents = parseInt(fundAmount, 10) * 100;
    if (Number.isNaN(amountInCents) || amountInCents <= 0) {
      toast.error(t('errors.invalidAmount') || 'Invalid amount');
      return;
    }

    try {
      await bankAccountService.fundSavingPot(fundingPot.savingPotId, {
        amountInCents,
      });
      toast.success(t('success.funded') || 'Successfully funded saving pot');
      setFundingPot(null);
      setFundAmount('');
      setIsFundModalOpen(false);
      loadPots();
    } catch (error: any) {
      console.error('Error funding saving pot:', error);
      toast.error(
        error?.response?.data?.message || t('errors.fundFailed') || 'Failed to fund saving pot',
      );
    }
  };

  const handleReleasePot = async () => {
    if (!releasingPot || !releaseAmount) {
      toast.error(t('errors.amountRequired') || 'Amount is required');
      return;
    }

    const amountInCents = parseInt(releaseAmount, 10) * 100;
    if (Number.isNaN(amountInCents) || amountInCents <= 0) {
      toast.error(t('errors.invalidAmount') || 'Invalid amount');
      return;
    }

    try {
      await bankAccountService.releaseFromSavingPot(releasingPot.savingPotId, {
        amountInCents,
      });
      toast.success(t('success.released') || 'Successfully released from saving pot');
      setReleasingPot(null);
      setReleaseAmount('');
      setIsReleaseModalOpen(false);
      loadPots();
    } catch (error: any) {
      console.error('Error releasing from saving pot:', error);
      toast.error(
        error?.response?.data?.message || t('errors.releaseFailed') || 'Failed to release from saving pot',
      );
    }
  };

  const openEditModal = (pot: PotWithBalances) => {
    setEditingPot({ ...pot });
    setIsEditModalOpen(true);
  };

  const openBreakModal = (pot: PotWithBalances) => {
    setBreakingPot(pot);
    setIsBreakModalOpen(true);
  };

  const openFundModal = (pot: PotWithBalances) => {
    setFundingPot(pot);
    setFundAmount('');
    setIsFundModalOpen(true);
  };

  const openReleaseModal = (pot: PotWithBalances) => {
    setReleasingPot(pot);
    setReleaseAmount('');
    setIsReleaseModalOpen(true);
  };

  const activePots = pots.filter((pot) => pot.isActive);
  const inactivePots = pots.filter((pot) => !pot.isActive);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="mb-4 text-lg text-gray-600">{t('loading') || 'Loading...'}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {!hideHeader && (
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          primaryButton={
            <div className="flex items-center gap-2">
              <Button
                variant="primary-dark"
                onClick={handleRunSavingPotAutoDeposits}
                disabled={isRunningAutoDeposits}>
                <TbArrowRight />
                {isRunningAutoDeposits
                  ? t('runningAutoDeposits') || 'Running...'
                  : t('runAutoDeposits') || 'Run Auto-Deposits'}
              </Button>
              <Button variant="primary-dark" onClick={() => navigate('/financial/pots/create')}>
                <TbPlus />
                {t('addPot')}
              </Button>
            </div>
          }
        />
      )}

      {hideHeader && (
        <div className="mb-4 flex justify-end">
          <Button
            variant="primary-dark"
            onClick={handleRunSavingPotAutoDeposits}
            disabled={isRunningAutoDeposits}>
            <TbArrowRight />
            {isRunningAutoDeposits
              ? t('runningAutoDeposits') || 'Running...'
              : t('runAutoDeposits') || 'Run Auto-Deposits'}
          </Button>
        </div>
      )}

      <div className="space-y-8">
        {/* Active Pots */}
        {activePots.length > 0 && (
          <div>
            <h2 className="mb-4 text-lg font-semibold text-gray-900">{t('sections.active')}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {activePots.map((pot) => (
                <div
                  key={pot.savingPotId}
                  className="cursor-pointer rounded-xl border border-secondary-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                  onClick={() => navigate(`/financial/pots/${pot.savingPotId}`)}>
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: pot.color }}>
                        <TbPig className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{pot.name}</h3>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(pot.isActive)}`}>
                            {getStatusIcon(pot.isActive)}
                            <span className="ml-1">{getStatusLabel(pot.isActive)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      onClick={(e) => {
                        // Prevent card navigation when interacting with the action menu
                        e.stopPropagation();
                      }}
                    >
                      <ActionMenu>
                        <ActionMenuButton
                          icon={TbArrowDown}
                          text={t('fund') || 'Fund'}
                          onClick={() => openFundModal(pot)}
                          className="text-success hover:text-green-700"
                        />
                        <ActionMenuButton
                          icon={TbArrowUp}
                          text={t('release') || 'Release'}
                          onClick={() => openReleaseModal(pot)}
                          className="text-primary-300 hover:text-blue-700"
                        />
                        <ActionMenuButton
                          icon={TbEdit}
                          text={t('edit')}
                          onClick={() => openEditModal(pot)}
                        />
                        <ActionMenuButton
                          icon={TbAlertCircle}
                          text={t('break')}
                          onClick={() => openBreakModal(pot)}
                          className="text-secondary hover:text-orange-400"
                        />
                        <ActionMenuButton
                          icon={TbTrash}
                          text={t('delete')}
                          onClick={() => handleDeletePot(pot)}
                          className="text-danger hover:text-red-700"
                        />
                      </ActionMenu>
                    </div>
                  </div>


                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('currentBalance')}</span>
                      <span
                        className={`font-semibold ${pot.totalBalance < 0 ? 'text-danger' : 'text-success'}`}>
                        {formatBalance(pot.totalBalance, pot.currency, i18n.language)}
                      </span>
                    </div>

                    {pot.targetAmount && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">{t('targetAmount')}</span>
                        <span className="font-semibold text-gray-900">
                          {formatBalance(pot.targetAmount, pot.currency, i18n.language)}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('participants')}</span>
                      <div className="flex items-center space-x-1">
                        <TbUsers className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium">
                          {(pot.participantIds ?? []).length}
                        </span>
                      </div>
                    </div>


                    {pot.targetAmount && (
                      <div className="h-2 w-full rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                          style={{
                            width: `${Math.min(100, Math.max(0, (pot.totalBalance / pot.targetAmount) * 100))}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inactive Pots */}
        {inactivePots.length > 0 && (
          <div>
            <h2 className="mb-4 text-lg font-semibold text-gray-900">{t('sections.broken')}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {inactivePots.map((pot) => (
                <div
                  key={pot.savingPotId}
                  className="cursor-pointer rounded-xl border border-secondary-200 bg-white p-6 opacity-75 shadow-sm transition-shadow hover:shadow-md"
                  onClick={() => navigate(`/financial/pots/${pot.savingPotId}`)}>
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: pot.color }}>
                        <TbAlertCircle className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{pot.name}</h3>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(pot.isActive)}`}>
                          {getStatusIcon(pot.isActive)}
                          <span className="ml-1">{getStatusLabel(pot.isActive)}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('finalBalance')}</span>
                      <span className="font-semibold text-gray-600">
                        {formatBalance(pot.totalBalance, pot.currency, i18n.language)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {pots.length === 0 && (
          <div className="py-12 text-center">
            <TbPig className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-medium text-gray-900">{t('emptyState.title')}</h3>
            <p className="mt-2 text-sm text-gray-500">{t('emptyState.description')}</p>
            <div className="mt-4">
              <Button variant="primary-dark" onClick={() => navigate('/financial/pots/create')}>
                <TbPlus />
                {t('addFirstPot')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Pot Modal */}
      <ModalLayout
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('createModal.title')}</h2>
          <div className="space-y-4">
            <Input
              id="pot-name"
              label={t('createModal.name')}
              type="text"
              placeholder={t('createModal.namePlaceholder')}
              value={newPot.name}
              onChange={(e) => setNewPot((prev) => ({ ...prev, name: e.target.value }))}
            />
            <Labeled label={t('createModal.targetAmount')}>
              <CurrencyInput
                id="target-amount"
                type="text"
                debounced
                placeholder={t('createModal.targetAmountPlaceholder')}
                handleChange={(value) => {
                  if (Number.isFinite(value)) {
                    setNewPotTargetAmountCents(Math.floor(value * 100));
                  } else {
                    setNewPotTargetAmountCents(null);
                  }
                }}
              />
            </Labeled>
            <Labeled label={t('createModal.currency') || 'Currency'}>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newPot.currency}
                onChange={(e) => setNewPot((prev) => ({ ...prev, currency: e.target.value as Currency }))}>
                <option value={Currency.EUR}>EUR</option>
                <option value={Currency.USD}>USD</option>
                <option value={Currency.GBP}>GBP</option>
              </select>
            </Labeled>
            <Labeled label={t('createModal.color')}>
              <div className="grid grid-cols-4 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 ${
                      newPot.color === color.value ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setNewPot((prev) => ({ ...prev, color: color.value }))}
                  />
                ))}
              </div>
            </Labeled>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleCreatePot}>
                {t('create')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>

      {/* Edit Pot Modal */}
      <ModalLayout
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('editModal.title')}</h2>
          <div className="space-y-4">
            <Input
              id="edit-pot-name"
              label={t('editModal.name')}
              type="text"
              placeholder={t('editModal.namePlaceholder')}
              value={editingPot?.name || ''}
              onChange={(e) =>
                setEditingPot((prev) => (prev ? { ...prev, name: e.target.value } : null))
              }
            />
            <Input
              id="edit-target-amount"
              label={t('editModal.targetAmount')}
              type="number"
              placeholder={t('editModal.targetAmountPlaceholder')}
              value={editingPot?.targetAmount ? (editingPot.targetAmount / 100).toString() : ''}
              onChange={(e) =>
                setEditingPot((prev) =>
                  prev
                    ? {
                        ...prev,
                        targetAmount: e.target.value
                          ? parseInt(e.target.value, 10) * 100
                          : null,
                      }
                    : null,
                )
              }
            />
            <Labeled label={t('editModal.color')}>
              <div className="grid grid-cols-4 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 ${
                      editingPot?.color === color.value ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() =>
                      setEditingPot((prev) => (prev ? { ...prev, color: color.value } : null))
                    }
                  />
                ))}
              </div>
            </Labeled>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleEditPot}>
                {t('update')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>

      {/* Break Pot Modal */}
      <ModalLayout
        open={isBreakModalOpen}
        setOpen={setIsBreakModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold text-secondary">{t('breakModal.title')}</h2>
          <div className="space-y-4">
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-start">
                <TbAlertCircle className="mr-3 mt-0.5 h-5 w-5 text-secondary" />
                <div>
                  <h3 className="text-sm font-medium text-secondary">{t('breakModal.warning')}</h3>
                  <p className="mt-1 text-sm text-secondary">{t('breakModal.description')}</p>
                </div>
              </div>
            </div>

            {breakingPot && (
              <div className="rounded-lg bg-gray-50 p-4">
                <h4 className="mb-2 font-medium text-gray-900">{t('breakModal.potDetails')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('potName')}:</span>
                    <span className="font-medium">{breakingPot.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('currentBalance')}:</span>
                    <span
                      className={`font-medium ${breakingPot.totalBalance < 0 ? 'text-danger' : 'text-success'}`}>
                      {formatBalance(breakingPot.totalBalance, breakingPot.currency, i18n.language)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('participants')}:</span>
                    <span className="font-medium">
                      {breakingPot.participants.filter((p) => p.isActive).length}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsBreakModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button
                variant="primary-dark"
                onClick={handleBreakPot}
                className="bg-orange-600 hover:bg-orange-700">
                {t('break')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>

      {/* Fund Pot Modal */}
      <ModalLayout
        open={isFundModalOpen}
        setOpen={setIsFundModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('fundModal.title') || 'Fund Saving Pot'}</h2>
          <div className="space-y-4">
            {fundingPot && (
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-600">
                  {t('fundModal.description') || 'Move funds from your wallet to this saving pot'}
                </p>
                <p className="mt-2 font-medium">{fundingPot.name}</p>
              </div>
            )}
            <Input
              id="fund-amount"
              label={t('fundModal.amount') || 'Amount'}
              type="number"
              placeholder={t('fundModal.amountPlaceholder') || 'Enter amount'}
              value={fundAmount}
              onChange={(e) => setFundAmount(e.target.value)}
            />
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsFundModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleFundPot}>
                {t('fundModal.confirm') || 'Fund'}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>

      {/* Release Pot Modal */}
      <ModalLayout
        open={isReleaseModalOpen}
        setOpen={setIsReleaseModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">
            {t('releaseModal.title') || 'Release from Saving Pot'}
          </h2>
          <div className="space-y-4">
            {releasingPot && (
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-600">
                  {t('releaseModal.description') || 'Move funds from this saving pot back to your wallet'}
                </p>
                <p className="mt-2 font-medium">{releasingPot.name}</p>
              </div>
            )}
            <Input
              id="release-amount"
              label={t('releaseModal.amount') || 'Amount'}
              type="number"
              placeholder={t('releaseModal.amountPlaceholder') || 'Enter amount'}
              value={releaseAmount}
              onChange={(e) => setReleaseAmount(e.target.value)}
            />
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsReleaseModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleReleasePot}>
                {t('releaseModal.confirm') || 'Release'}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>
    </>
  );
}
